import api from './client';
import type { ApiResponse } from '../types/analiseCompra';
import type { DimDef, MeasureDef } from '../components/dataExplorer/pivotEngine';
import type { DatasetConfig } from '../components/dataExplorer/types';

// ─────────────────────────────────────────────────────────────────────────────
// estatisticasModelos.ts — modelos de dados CADASTRADOS pelo admin (Fase 5).
// Contrato com /api/estatisticas/modelos (CRUD/preview = admin; listar = quem usa
// o Explorador). `construirDatasetConfig` é a ponte: vira o mapeamento salvo no
// DatasetConfig que a engine genérica consome — igual aos modelos built-in.
// ─────────────────────────────────────────────────────────────────────────────

/** Papel de uma coluna do SQL no pivot. */
export type PapelColuna = 'dim' | 'measure' | 'ignore';
export type FmtMedida = 'money' | 'int' | 'pct';
export type AggMedida = 'sum' | 'avg' | 'count' | 'min' | 'max';

/** Mapeamento de UMA coluna do resultado do SQL. */
export interface ColunaMapeada {
  field: string;        // nome da coluna (alias do SQL)
  role: PapelColuna;
  label: string;
  fmt?: FmtMedida;      // só measure
  agg?: AggMedida;      // só measure
}

/** Mapeamento completo (vira DatasetConfig). v1 sem eixo de tempo. */
export interface MapeamentoModelo {
  colunas: ColunaMapeada[];
}

/**
 * Tipo de um parâmetro (filtro) do modelo. Define o input no Explorador e o tipo
 * do bind no Firebird. 'lista' = multiselect populado por subselect.
 */
export type TipoParametro =
  | 'texto'    // VARCHAR
  | 'numero'   // INTEGER
  | 'decimal'  // DOUBLE
  | 'data'     // DATE (dia/mês/ano)
  | 'mesAno'   // INTEGER AAAAMM (competência)
  | 'ano'      // INTEGER (só o ano)
  | 'lista'    // IN (...) via subselect (multiselect)
  | 'booleano'; // CHAR 'S'/'N'

/** Parâmetro (filtro): o SQL usa @nome; o Explorador renderiza 1 input por tipo. */
export interface ParametroModelo {
  nome: string;
  label: string;
  tipo: TipoParametro;
  /** Só p/ tipo 'lista': SQL `SELECT valor, rotulo FROM ...` que popula o multiselect. */
  opcoesSql?: string;
  /** Valor inicial: token (hoje/inicioMes/anoAtual…) resolvido no load, ou literal. */
  padrao?: string;
}

/** Uma opção de um parâmetro 'lista' (col1 = valor enviado, col2 = rótulo exibido). */
export interface OpcaoLista {
  valor: string;
  label: string;
}

/** Item do dropdown do Explorador. */
export interface ModeloSalvo {
  id: number;
  nome: string;
  mapeamento: MapeamentoModelo;
  parametros: ParametroModelo[];
}

/** Item completo da tela de admin (inclui o SQL). */
export interface ModeloSalvoAdmin extends ModeloSalvo {
  sql: string;
  ativo: boolean;
  criadoPor?: string | null;
  criadoEm?: string;
  atualizadoEm?: string;
}

export interface PreviewResultado {
  colunas: string[];
  linhas: Array<Record<string, unknown>>;
  truncado: boolean;
}

export interface ModeloPayload {
  nome: string;
  sql: string;
  mapeamento: MapeamentoModelo;
  parametros: ParametroModelo[];
  ativo: boolean;
}

/** Parâmetro + valor de teste, usado no preview do admin. */
export interface ParametroComValor extends ParametroModelo {
  valor?: string;
}

class EstatisticasModelosApi {
  /** Modelos ativos pro dropdown (sem o SQL). */
  async listar(): Promise<ModeloSalvo[]> {
    const r = await api.get<ApiResponse<ModeloSalvo[]>>('/estatisticas/modelos');
    return r.data.data ?? [];
  }

  /** Lista completa (admin) com SQL + inativos. */
  async gerenciar(): Promise<ModeloSalvoAdmin[]> {
    const r = await api.get<ApiResponse<ModeloSalvoAdmin[]>>('/estatisticas/modelos/gerenciar');
    return r.data.data ?? [];
  }

  async criar(p: ModeloPayload): Promise<number> {
    const r = await api.post<ApiResponse<{ id: number }>>('/estatisticas/modelos', p);
    return r.data.data.id;
  }

  async atualizar(id: number, p: ModeloPayload): Promise<void> {
    await api.put(`/estatisticas/modelos/${id}`, p);
  }

  async excluir(id: number): Promise<void> {
    await api.delete(`/estatisticas/modelos/${id}`);
  }

  /** Executa o SQL (blindado, com valores de teste dos parâmetros) e devolve colunas + amostra. */
  async preview(sql: string, parametros: ParametroComValor[] = [], limite = 100): Promise<PreviewResultado> {
    const r = await api.post<ApiResponse<PreviewResultado>>('/estatisticas/modelos/preview', { sql, limite, parametros });
    return r.data.data;
  }

  /** Testa um SQL de OPÇÕES (admin): col1 = valor, col2 = rótulo. Pro tipo 'lista'. */
  async opcoesPreview(sql: string): Promise<OpcaoLista[]> {
    const r = await api.post<ApiResponse<OpcaoLista[]>>('/estatisticas/modelos/opcoes', { sql });
    return r.data.data ?? [];
  }

  /** Opções de um parâmetro 'lista' de um modelo salvo — popula o multiselect no Explorador. */
  async opcoesDoModelo(id: number, param: string): Promise<OpcaoLista[]> {
    const r = await api.get<ApiResponse<OpcaoLista[]>>(`/estatisticas/modelos/${id}/opcoes`, { params: { param } });
    return r.data.data ?? [];
  }
}

export const estatisticasModelosApi = new EstatisticasModelosApi();

/**
 * Constrói um DatasetConfig a partir do mapeamento salvo. Dimensões e medidas
 * saem das colunas marcadas; default = 1ª dim nas linhas + 1ª medida nos valores.
 * Sem eixo de tempo no v1 (o Explorador esconde Evolutiva/Tendência).
 */
export function construirDatasetConfig(mapa: MapeamentoModelo): DatasetConfig {
  const cols = mapa?.colunas ?? [];
  const dimsArr = cols.filter((c) => c.role === 'dim');
  const measArr = cols.filter((c) => c.role === 'measure');

  const dims: Record<string, DimDef> = {};
  for (const c of dimsArr) {
    const label = c.label?.trim() || c.field;
    dims[c.field] = { key: c.field, label, plural: label };
  }

  const meas: Record<string, MeasureDef> = {};
  for (const c of measArr) {
    meas[c.field] = {
      key: c.field,
      label: c.label?.trim() || c.field,
      fmt: c.fmt ?? 'int',
      defaultAgg: c.agg ?? 'sum',
    };
  }

  // Modelo cadastrado (SQL) abre PLANO: cada registro é 1 linha, TODAS as dims
  // viram colunas e TODAS as medidas viram colunas de valor (o registro inteiro —
  // ex.: curva ABC). Pra agrupar, o usuário arrasta dims para "Agrupar".
  const rows = dimsArr.map((c) => c.field);
  const vals = measArr.map((c) => ({ field: c.field, agg: c.agg ?? 'sum', calc: 'none' as const }));

  return { dims, meas, defaults: { view: 'matriz', groupBy: [], rows, cols: [], vals } };
}
